import { useState } from 'react';
import type { RadioStation } from '@/mocks/radios';
import { useAudioPlayer } from '@/context/AudioContext';
import { getRfwScore } from '@/mocks/rfwScores';
import { getSocialStats } from '@/mocks/rfwSocial';
import RfwScoreCard from '@/components/feature/RfwScoreCard';
import ShareModal from '@/components/feature/ShareModal';
import LikeButton from '@/components/feature/LikeButton';
import ViralStats from '@/components/feature/ViralStats';
import RadioBadges from './RadioBadges';
import { useNowPlaying } from '@/hooks/useNowPlaying';
import {
  canPlayStream,
  shouldFallbackToRfw,
  isTerminalStatus,
  isTransientStatus,
  technicalStreamStatusShortLabels,
  technicalStreamStatusColors,
  technicalStreamStatusIcons,
} from '@/types/streaming';
import { SwalAlert } from '@/hooks/useSweetAlert';

interface RadioCoverHeaderProps {
  station: RadioStation;
  followers: number;
  programsCount: number;
  hostsCount: number;
  onOpenCabina: () => void;
}

export default function RadioCoverHeader({ station, followers, programsCount, hostsCount, onOpenCabina }: RadioCoverHeaderProps) {
  const { play } = useAudioPlayer();
  const [following, setFollowing] = useState(false);
  const [notificationsOn, setNotificationsOn] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // ── Now Playing desde backend PHP ──
  const { nowPlaying, status: stationStatus, isStale, onlineLabel } = useNowPlaying({
    radioId: String(station.id),
  });

  const rfwScore = getRfwScore('radio', station.id);
  const socialStats = getSocialStats('radio', station.id);
  const techStatus = station.technicalStreamStatus;
  const statusColors = technicalStreamStatusColors[techStatus];
  const statusIcon = technicalStreamStatusIcons[techStatus];
  const statusLabel = technicalStreamStatusShortLabels[techStatus];

  const coverUrl = "https://readdy.ai/api/search-image?query=Radio%20station%20cover%20banner%20background%20with%20abstract%20sound%20waves%20and%20colorful%20equalizer%20bars%2C%20warm%20vibrant%20orange%20and%20amber%20tones%20gradient%2C%20modern%20dynamic%20broadcast%20visual%2C%20music%20energy%20flow%2C%20artistic%20digital%20background%2C%20wide%20panoramic%20composition%2C%20clean%20minimal%20style&width=1440&height=400&seq=cover-radio-v3&orientation=landscape";

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  const handleFollow = () => { setFollowing(!following); };

  const handleListen = () => {
    if (isTerminalStatus(techStatus) || isTransientStatus(techStatus)) {
      SwalAlert.info(
        techStatus === 'suspended' ? 'Esta radio está suspendida.' :
        techStatus === 'error' ? 'Hay un error técnico con el streaming.' :
        techStatus === 'deletion_pending' ? 'Esta radio será eliminada pronto.' :
        techStatus === 'deleted' ? 'Esta radio ya no está disponible.' :
        techStatus === 'pending_provisioning' ? 'El servidor está siendo aprovisionado.' :
        'El servidor se está configurando.',
        'No disponible'
      );
      return;
    }
    play(station);
  };

  const getPlayButtonLabel = () => {
    if (canPlayStream(techStatus)) return 'Escuchar';
    if (shouldFallbackToRfw(techStatus)) return 'Escuchar RFW Radio';
    if (isTransientStatus(techStatus)) return 'Configurando...';
    return 'No disponible';
  };

  const getPlayButtonIcon = () => {
    if (canPlayStream(techStatus)) return 'ri-headphone-fill';
    if (shouldFallbackToRfw(techStatus)) return 'ri-shuffle-line';
    if (isTransientStatus(techStatus)) return 'ri-loader-4-line animate-spin';
    return 'ri-forbid-2-line';
  };

  const getPlayButtonStyle = () => {
    if (canPlayStream(techStatus)) return 'bg-accent-500 text-background-50 dark:text-foreground-950 hover:bg-accent-600';
    if (shouldFallbackToRfw(techStatus)) return 'bg-amber-500 text-white hover:bg-amber-600';
    return 'bg-foreground-300 text-white cursor-not-allowed';
  };

  const streamStatusColor = statusColors?.dot || 'bg-foreground-300';

  return (
    <>
      {/* Cover Photo */}
      <div className="relative w-full h-[280px] md:h-[380px] bg-background-200/70 overflow-hidden">
        <img
          src={coverUrl}
          alt={`${station.name} portada`}
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent"></div>
      </div>

      {/* Profile Info Bar */}
      <div className="bg-background-50 border-b border-background-200/70">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 -mt-16 sm:-mt-20 pb-5 relative z-10">
            {/* Logo */}
            <div className="relative">
              <div className="w-28 h-28 md:w-36 md:h-36 rounded-full border-4 border-background-50 bg-background-50 overflow-hidden shadow-lg">
                <img
                  src={station.logoUrl}
                  alt={station.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Stream Status Badge */}
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-0.5 rounded-full text-white text-[10px] font-bold whitespace-nowrap"
                style={{ backgroundColor: canPlayStream(techStatus) ? '#ef4444' : shouldFallbackToRfw(techStatus) ? '#f59e0b' : '#9ca3af' }}
              >
                {canPlayStream(techStatus) && (
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                )}
                <i className={`${statusIcon} text-[10px]`}></i>
                {statusLabel}
              </div>
            </div>

            {/* Name + Info */}
            <div className="flex-1 min-w-0 pt-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 whitespace-nowrap">
                  {station.name}
                </h1>
                {/* Technical status indicator */}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${statusColors?.bg || 'bg-foreground-100'} ${statusColors?.text || 'text-foreground-600'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statusColors?.dot || 'bg-foreground-300'}`}></span>
                  {statusLabel}
                </span>
              </div>
              <p className="text-foreground-500 text-sm mt-0.5">
                {station.frequency} · {station.city}, {station.state} · {station.category} · {station.language}
              </p>

              {/* Stream metadata */}
              {station.currentMetadata && (
                <p className="text-xs text-foreground-400 mt-1 flex items-center gap-1.5">
                  <i className="ri-music-line text-[11px]"></i>
                  <span className="truncate">{station.currentMetadata}</span>
                </p>
              )}

              {/* ── Now Playing en vivo (backend PHP) ── */}
              {nowPlaying && (
                <div className={`mt-2 flex items-center gap-2 flex-wrap ${isStale ? 'opacity-60' : ''}`}>
                  {/* Artwork */}
                  {nowPlaying.artwork_url && (
                    <div className="w-9 h-9 rounded-lg overflow-hidden bg-background-200 shrink-0">
                      <img
                        src={nowPlaying.artwork_url}
                        alt={nowPlaying.title || 'Track actual'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  {/* Track info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${nowPlaying.online ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'} shrink-0`}></span>
                      <span className="text-xs font-semibold text-foreground-800 truncate">
                        {nowPlaying.title || 'Sin título'}
                      </span>
                      {nowPlaying.artist && (
                        <span className="text-[11px] text-foreground-500 truncate">
                          · {nowPlaying.artist}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-foreground-400 flex items-center gap-1">
                        <i className="ri-headphone-line text-[9px]"></i>
                        {nowPlaying.listeners_current.toLocaleString()} oyentes
                      </span>
                      {isStale && (
                        <span className="text-[10px] text-amber-600 font-medium flex items-center gap-1">
                          <i className="ri-time-line text-[9px]"></i>
                          Puede estar desactualizado
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-2">
                <RadioBadges station={station} />
              </div>

              {/* Stats + RFW Score */}
              <div className="flex items-center gap-2.5 md:gap-5 mt-3 text-sm flex-wrap">
                <div className="flex items-center gap-1.5 text-foreground-700">
                  <i className="ri-user-heart-line text-base"></i>
                  <span className="font-semibold">{formatNumber(followers)}</span>
                  <span className="text-foreground-500">Seguidores</span>
                </div>
                <div className="flex items-center gap-1.5 text-foreground-700">
                  <i className="ri-headphone-line text-base"></i>
                  <span className="font-semibold">{formatNumber(station.technicalStreamStatus === 'active' ? station.listenerCount : station.listeners)}</span>
                  <span className="text-foreground-500">Oyentes</span>
                </div>
                <div className="flex items-center gap-1.5 text-foreground-700">
                  <i className="ri-calendar-check-line text-base"></i>
                  <span className="font-semibold">{programsCount}</span>
                  <span className="text-foreground-500">Programas</span>
                </div>
                <div className="flex items-center gap-1.5 text-foreground-700">
                  <i className="ri-mic-line text-base"></i>
                  <span className="font-semibold">{hostsCount}</span>
                  <span className="text-foreground-500">Locutores</span>
                </div>

                {/* RFW Score inline badge */}
                {rfwScore && (
                  <div className="hidden sm:flex items-center gap-2 pl-4 border-l border-background-300/60">
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-flashlight-fill text-accent-600 text-sm"></i>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-extrabold text-foreground-950">{rfwScore.currentScore.toLocaleString()}</span>
                        <span className="text-[11px] text-foreground-400">RFW Score</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold text-foreground-700">#{rfwScore.rankingPosition} ranking</span>
                        <span className={`text-[11px] font-semibold ${rfwScore.scoreChange > 0 ? 'text-emerald-600' : rfwScore.scoreChange < 0 ? 'text-red-500' : 'text-foreground-400'}`}>
                          {rfwScore.scoreChange > 0 ? '+' : ''}{rfwScore.scoreChange}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Viral Stats Row */}
              {socialStats && (
                <div className="mt-2 pt-2 border-t border-background-100">
                  <ViralStats
                    shares={socialStats.shares}
                    reach={socialStats.reach}
                    interactions={socialStats.interactions}
                    trendPercent={socialStats.trendPercent}
                    trendDirection={socialStats.trendDirection}
                    compact
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* === ACTION BUTTONS BAR === */}
      <div className="bg-background-50 border-b border-background-200/70 sticky top-[72px] z-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3.5 space-y-2.5">
          {/* Botones principales */}
          <div className="flex flex-wrap justify-center gap-2">
            {/* Escuchar */}
            <button
              onClick={handleListen}
              disabled={isTerminalStatus(techStatus) || isTransientStatus(techStatus)}
              className={`flex items-center gap-1.5 px-3 py-2 md:px-4 md:py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${getPlayButtonStyle()} ${(isTerminalStatus(techStatus) || isTransientStatus(techStatus)) ? 'opacity-60' : ''}`}
            >
              <i className={`${getPlayButtonIcon()} text-base md:text-lg`}></i>
              <span>{getPlayButtonLabel()}</span>
            </button>

            {/* Entrar a Cabina */}
            <button
              onClick={onOpenCabina}
              className="flex items-center gap-1.5 px-3 py-2 md:px-4 md:py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap bg-gradient-to-br from-red-500 to-rose-600 text-white hover:from-red-600 hover:to-rose-700 relative overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-1.5">
                <div className="relative">
                  <i className="ri-broadcast-fill text-base md:text-lg"></i>
                  <span className="absolute -top-0.5 -right-1.5 w-2 h-2 bg-white rounded-full animate-pulse"></span>
                </div>
                <span>Entrar a Cabina</span>
              </span>
            </button>

            {/* Seguir */}
            <button
              onClick={handleFollow}
              className={`flex items-center gap-1.5 px-3 py-2 md:px-4 md:py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
                following
                  ? 'bg-secondary-100 text-secondary-700 border border-secondary-200 hover:bg-secondary-200/70'
                  : 'bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600'
              }`}
            >
              <i className={`${following ? 'ri-user-follow-fill' : 'ri-user-follow-line'} text-base md:text-lg`}></i>
              <span>{following ? 'Siguiendo' : 'Seguir'}</span>
            </button>

            {/* Me Gusta */}
            <LikeButton
              entityId={station.id}
              entityType="radio"
              initialLikes={socialStats?.likes || 0}
              initialLiked={false}
              size="md"
            />

            {/* Compartir */}
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 md:px-4 md:py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap bg-background-100 text-foreground-700 border border-background-300/60 hover:bg-background-200/70"
            >
              <i className="ri-share-forward-line text-base md:text-lg"></i>
              <span>Compartir</span>
            </button>

            {/* Notificaciones */}
            <button
              onClick={() => setNotificationsOn(!notificationsOn)}
              className={`flex items-center gap-1.5 px-3 py-2 md:px-4 md:py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
                notificationsOn
                  ? 'bg-secondary-100 text-secondary-700 border border-secondary-200 hover:bg-secondary-200/70'
                  : 'bg-background-100 text-foreground-700 border border-background-300/60 hover:bg-background-200/70'
              }`}
            >
              <i className={`${notificationsOn ? 'ri-notification-3-fill' : 'ri-notification-3-line'} text-base md:text-lg`}></i>
              <span>{notificationsOn ? 'Notificaciones' : 'Notificar'}</span>
            </button>
          </div>

          {/* RFW Score Card */}
          {rfwScore && (
            <div className="w-full">
              <RfwScoreCard
                score={rfwScore}
                entityLabel="Radio"
                variant="compact"
              />
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        entityName={station.name}
        entitySubtitle={station.frequency}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </>
  );
}